import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  useTheme,
  useMediaQuery,
  Avatar
} from '@mui/material';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { useGarage } from '../../contexts/GarageContext';
import AppointmentCard from './AppointmentCard';
import AppointmentSummaryModal from '../garage-admin/AppointmentSummaryModal';
import NewAppointmentModal from '../garage-admin/NewAppointmentModal';
import CategoryLegend from './CategoryLegend';
import { CATEGORY_CONFIG } from '../../utils/categoryConstants';

const WeekView = ({ 
  currentDate, 
  selectedDate, 
  appointments, 
  onDateSelect,
  getCategoryCounts,
  availableProducts,
  availableAddons,
  isWithinBusinessHours,
  isBusinessDay,
  onRefreshCalendar
}) => {
  const theme = useTheme();
  const { isDark } = useAppTheme();
  const { selectedGarage } = useGarage();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Local state for appointment details modal
  const [appointmentDetailsOpen, setAppointmentDetailsOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // Local state for new appointment modal
  const [newAppointmentOpen, setNewAppointmentOpen] = useState(false);
  const [selectedSlotDate, setSelectedSlotDate] = useState(null);
  const [selectedSlotTime, setSelectedSlotTime] = useState(null);
  
  // State for current time to trigger re-renders
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Handle appointment card click - open appointment details
  const handleAppointmentClick = (appointment, event) => {
    event?.stopPropagation();
    setSelectedAppointment(appointment.originalBooking);
    setAppointmentDetailsOpen(true);
  };

  // Handle empty slot click - open new appointment modal
  const handleSlotClick = (date, time, event) => {
    event?.stopPropagation();
    
    // Check if the time slot is in the future
    const now = new Date();
    const slotDateTime = new Date(date);
    const [hours, minutes] = time.split(':').map(Number);
    slotDateTime.setHours(hours, minutes, 0, 0);
    
    // Only allow future time slots
    if (slotDateTime <= now) {
      return; // Don't open modal for past time slots
    }

    // Check if date is a business day
    if (isBusinessDay && !isBusinessDay(date)) {
      return; // Don't open modal for closed days
    }

    // Check if time is within business hours
    if (isWithinBusinessHours && !isWithinBusinessHours(date, time)) {
      return; // Don't open modal for closed hours
    }
    
    setSelectedSlotDate(date);
    setSelectedSlotTime(time);
    setNewAppointmentOpen(true);
  };

  // Handle appointment modal close
  const handleAppointmentDetailsClose = () => {
    setAppointmentDetailsOpen(false);
    setSelectedAppointment(null);
  };

  // Handle new appointment modal close
  const handleNewAppointmentClose = () => {
    setNewAppointmentOpen(false);
    setSelectedSlotDate(null);
    setSelectedSlotTime(null);
  };

  // Handle appointment creation success
  const handleAppointmentSuccess = () => {
    handleNewAppointmentClose();
    // Refresh calendar to show new appointment
    if (onRefreshCalendar) {
      onRefreshCalendar();
    }
  };

  // Handle appointment data change
  const handleAppointmentDataChange = () => {
    // Refresh calendar to show updated appointment
    if (onRefreshCalendar) {
      onRefreshCalendar();
    }
  };

  // Get week dates starting from Monday
  const getWeekDates = () => {
    const start = new Date(currentDate);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Monday
    start.setDate(diff);
    
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      weekDates.push(date);
    }
    return weekDates;
  };

  // Generate time slots (8 AM to 6 PM)
  const getTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour < 18; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return slots;
  };

  // Get appointments for a specific date
  const getAppointmentsForDate = (date) => {
    const dateStr = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
    return appointments.filter(apt => apt.date === dateStr);
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is selected
  const isSelected = (date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  // Check if it's current time for a specific date
  const isCurrentTime = (date, slotTime) => {
    const today = currentTime.toISOString().split('T')[0];
    const dateStr = date.toISOString().split('T')[0];
    
    if (today !== dateStr) return false;
    
    const currentTimeStr = currentTime.toTimeString().substring(0, 5);
    return slotTime <= currentTimeStr && currentTimeStr < getNextSlotTime(slotTime);
  };

  // Get next slot time
  const getNextSlotTime = (currentSlot) => {
    const [hours, minutes] = currentSlot.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + 30;
    const nextHours = Math.floor(totalMinutes / 60);
    const nextMinutes = totalMinutes % 60;
    return `${nextHours.toString().padStart(2, '0')}:${nextMinutes.toString().padStart(2, '0')}`;
  };

  // Format time for display
  const formatTimeDisplay = (time) => {
    const [hours, minutes] = time.split(':');
    const hour12 = parseInt(hours) > 12 ? parseInt(hours) - 12 : parseInt(hours) === 0 ? 12 : parseInt(hours);
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Check if two appointments have any time conflict - if so, display side-by-side
  const appointmentsOverlap = (apt1, apt2) => {
    if (!apt1.startTime || !apt1.endTime || !apt2.startTime || !apt2.endTime) return false;
    
    const apt1Start = apt1.startTime;
    const apt1End = apt1.endTime;
    const apt2Start = apt2.startTime;
    const apt2End = apt2.endTime;
    
    return apt1Start < apt2End && apt2Start < apt1End;
  };

  // Calculate overlapping groups for appointments
  const getOverlappingGroups = (appointments) => {
    const groups = [];
    const processed = new Set();
    
    appointments.forEach((appointment, index) => {
      if (processed.has(index)) return;
      
      const group = [appointment];
      processed.add(index);
      
      // Find all overlapping appointments
      appointments.forEach((otherAppointment, otherIndex) => {
        if (otherIndex === index || processed.has(otherIndex)) return;
        
        const overlapsWithGroup = group.some(groupApp => 
          appointmentsOverlap(groupApp, otherAppointment)
        );
        
        if (overlapsWithGroup) {
          group.push(otherAppointment);
          processed.add(otherIndex);
        }
      });
      
      groups.push(group);
    });
    
    return groups;
  };

  // Calculate appointment position, height, and width for overlapping
  const getAppointmentStyle = (appointment, dayAppointments) => {
    const startTime = appointment.startTime;
    const endTime = appointment.endTime;
    
    if (!startTime || !endTime) return null;
    
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    // 8 AM = 480 minutes, 6 PM = 1080 minutes
    const dayStartMinutes = 8 * 60;
    const dayEndMinutes = 18 * 60;
    
    if (startTotalMinutes < dayStartMinutes || startTotalMinutes >= dayEndMinutes) {
      return null; // Outside business hours
    }
    
    // Use responsive slot height that matches the actual slot heights
    const slotHeight = { xs: 35, sm: 38, md: 40 };
    const currentSlotHeight = window.innerWidth < 600 ? slotHeight.xs : 
                             window.innerWidth < 960 ? slotHeight.sm : slotHeight.md;
    
    const minutesFromStart = startTotalMinutes - dayStartMinutes;
    const duration = Math.min(endTotalMinutes, dayEndMinutes) - startTotalMinutes;
    
    const top = (minutesFromStart / 30) * currentSlotHeight;
    const height = Math.max((duration / 30) * currentSlotHeight, currentSlotHeight * 0.8);
    
    // Calculate overlapping layout
    const overlappingGroups = getOverlappingGroups(dayAppointments);
    const currentGroup = overlappingGroups.find(group => 
      group.some(apt => apt.id === appointment.id)
    );
    
    if (!currentGroup || currentGroup.length === 1) {
      // No overlap - full width
      return { 
        top, 
        height, 
        left: 4, 
        right: 4, 
        width: 'calc(100% - 8px)'
      };
    }
    
    // Multiple overlapping appointments
    const groupSize = currentGroup.length;
    const appointmentIndex = currentGroup.findIndex(apt => apt.id === appointment.id);
    
    // Calculate width and position for overlapping
    const availableWidth = 100; // percentage
    const appointmentWidth = availableWidth / groupSize;
    const leftOffset = appointmentIndex * appointmentWidth;
    
    return { 
      top, 
      height, 
      left: `${leftOffset}%`, 
      width: `${appointmentWidth - 1}%` // Small gap between overlapping events
    };
  };

  const weekDates = getWeekDates();
  const timeSlots = getTimeSlots();

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      p: { xs: 1, sm: 1.5, md: 2 }
    }}>
      {/* Category Legend */}
      <CategoryLegend compact={isMobile} availableProducts={availableProducts} />

      <Card sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* Scrollable container for headers and timeline */}
        <Box sx={{
          display: 'flex',
          flex: 1,
          overflowY: 'hidden',
          overflowX: 'auto',
          minHeight: 0
        }}>
          {/* Fixed Time column (header and slots) */}
          <Box sx={{
            width: 60,
            flexShrink: 0,
            position: { xs: 'sticky', sm: 'static' },
            left: { xs: 0, sm: 'auto' },
            zIndex: { xs: 10, sm: 1 },
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Time column header */}
            <Box sx={{ 
              width: 60,
              height: '100%', // Match the actual day headers height
              p: { xs: 1, sm: 1.5, md: 2 },
              borderRight: '1px solid', 
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isDark ? 'grey.800' : 'grey.50',
              boxSizing: 'border-box'
            }}>
              <Typography 
                variant="subtitle2" 
                color="text.secondary" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' } }}
              >
                Time
              </Typography>
            </Box>
            
            {/* Time slots */}
            <Box sx={{ 
              width: 60,
              p: 0,
              borderRight: '1px solid', 
              borderColor: 'divider',
              bgcolor: isDark ? 'grey.900' : 'grey.50',
              boxSizing: 'border-box'
            }}>
              {timeSlots.map((time, index) => {
                const isCurrentTimeSlot = weekDates.some(date => isCurrentTime(date, time));
                
                return (
                  <Box
                    key={time}
                    sx={{
                      height: { xs: 35, sm: 38, md: 40 },
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderBottom: index % 2 === 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      bgcolor: isCurrentTimeSlot ? 'primary.50' : 'transparent',
                      boxSizing: 'border-box'
                    }}
                  >
                    {index % 2 === 0 && (
                      <Typography 
                        variant="caption" 
                        color={isCurrentTimeSlot ? 'primary.main' : 'text.secondary'}
                        sx={{ 
                          fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' }, 
                          fontWeight: isCurrentTimeSlot ? 'bold' : 500,
                          textAlign: 'center'
                        }}
                      >
                        {formatTimeDisplay(time)}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Scrollable Day headers and columns */}
          <Box sx={{
            flex: 1,
            overflowX: { xs: 'auto', sm: 'hidden' },
            overflowY: 'hidden', // Block vertical scrolling
            minWidth: { xs: 'max-content', sm: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            '&::-webkit-scrollbar': {
              height: '6px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: isDark ? 'grey.600' : 'grey.400',
              borderRadius: '3px'
            },
            scrollbarWidth: { xs: 'thin', sm: 'none' }
          }}>
            {/* Day headers */}
            <Box sx={{
              display: 'flex',
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: isDark ? 'grey.800' : 'grey.50',
              position: { xs: 'sticky', sm: 'static' },
              top: 0,
              zIndex: { xs: 9, sm: 1 }
            }}>
              {weekDates.map((date) => {
                const isTodayDate = isToday(date);
                const isSelectedDate = isSelected(date);
                const dayAppointments = getAppointmentsForDate(date);
                
                return (
                  <Box
                    key={date.toISOString()}
                    onClick={() => onDateSelect(date)}
                    sx={{
                      flex: { xs: '0 0 120px', sm: 1 },
                      minHeight: 60, // Match time header height
                      p: { xs: 1, sm: 1.5, md: 2 },
                      textAlign: 'center',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      bgcolor: isSelectedDate 
                        ? 'primary.50' 
                        : isTodayDate
                          ? (isDark ? 'rgba(255, 143, 0, 0.06)' : 'rgba(255, 179, 0, 0.04)')
                          : 'transparent',
                      // Professional today header styling
                      ...(isTodayDate && {
                        borderTop: `3px solid ${isDark ? '#ff8f00' : '#ffb300'}`,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: isDark
                            ? `linear-gradient(135deg, rgba(255, 143, 0, 0.03) 0%, rgba(255, 111, 0, 0.02) 100%)`
                            : `linear-gradient(135deg, rgba(255, 179, 0, 0.03) 0%, rgba(255, 152, 0, 0.02) 100%)`,
                          pointerEvents: 'none'
                        }
                      }),
                      '&:hover': {
                        bgcolor: isSelectedDate ? 'primary.100' : 'action.hover'
                      },
                      '&:last-child': {
                        borderRight: 'none'
                      },
                      transition: 'background-color 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        display: 'block',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontWeight: 500,
                        fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' }
                      }}
                    >
                      {date.toLocaleDateString('en-GB', { weekday: 'short' })}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      fontWeight="bold"
                      sx={{
                        // Professional today highlighting with responsive amber colors
                        color: isTodayDate 
                          ? '#ffffff'
                          : 'text.primary',
                        mt: 0.5,
                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
                        // Professional styling for today - centered
                        ...(isTodayDate && {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: { xs: 22, sm: 26, md: 30 },
                          height: { xs: 22, sm: 26, md: 30 },
                          borderRadius: '50%',
                          bgcolor: isDark ? '#ff8f00' : '#ffb300',
                          boxShadow: isDark 
                            ? '0 2px 6px rgba(255, 143, 0, 0.4), 0 1px 2px rgba(255, 143, 0, 0.3)'
                            : '0 2px 6px rgba(255, 179, 0, 0.35), 0 1px 2px rgba(255, 179, 0, 0.25)',
                          transform: 'scale(1.02)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          mx: 'auto' // Center horizontally
                        })
                      }}
                    >
                      {date.getDate()}
                    </Typography>
                    
                    {/* Category counts */}
                    {getCategoryCounts && dayAppointments.length > 0 && (
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: { xs: 0.25, md: 0.5 }, 
                        mt: 0.5,
                        flexWrap: 'wrap'
                      }}>
                        {(() => {
                          const counts = getCategoryCounts(dayAppointments);
                          
                          return Object.entries(counts)
                            .filter(([_, count]) => count > 0)
                            .map(([category, count]) => (
                              <Avatar
                                key={category}
                                sx={{
                                  width: { xs: 14, sm: 16, md: 18 },
                                  height: { xs: 14, sm: 16, md: 18 },
                                  fontSize: { xs: '0.5rem', sm: '0.55rem', md: '0.6rem' },
                                  fontWeight: 'bold',
                                  bgcolor: CATEGORY_CONFIG[category].color,
                                  color: 'white'
                                }}
                              >
                                {CATEGORY_CONFIG[category].letter}{count}
                              </Avatar>
                            ));
                        })()}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Day columns */}
            <Box sx={{
              display: 'flex',
              minHeight: '100%'
            }}>
              {weekDates.map((date, dayIndex) => {
                const dayAppointments = getAppointmentsForDate(date);
                
                return (
                  <Box
                    key={date.toISOString()}
                    sx={{
                      flex: { xs: '0 0 120px', sm: 1 },
                      borderRight: dayIndex < 6 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      position: 'relative',
                      minHeight: { 
                        xs: timeSlots.length * 17.5, 
                        sm: timeSlots.length * 19, 
                        md: timeSlots.length * 20 
                      },
                      boxSizing: 'border-box'
                    }}
                  >
                    {/* Time slot backgrounds */}
                    {timeSlots.map((time, slotIndex) => {
                      const isCurrentTimeSlot = isCurrentTime(date, time);
                      
                      // Check if this time slot is available
                      const now = new Date();
                      const slotDateTime = new Date(date);
                      const [hours, minutes] = time.split(':').map(Number);
                      slotDateTime.setHours(hours, minutes, 0, 0);
                      
                      const isPastSlot = slotDateTime <= now;
                      const isBusinessDayAvailable = !isBusinessDay || isBusinessDay(date);
                      const isWithinHours = !isWithinBusinessHours || isWithinBusinessHours(date, time);
                      const isSlotAvailable = !isPastSlot && isBusinessDayAvailable && isWithinHours;
                      
                      return (
                        <Box
                          key={time}
                          onClick={(e) => {
                            if (isSlotAvailable) {
                              handleSlotClick(date, time, e);
                            }
                          }}
                          sx={{
                            height: { xs: 35, sm: 38, md: 40 },
                            borderBottom: slotIndex % 2 === 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            bgcolor: isCurrentTimeSlot 
                              ? 'primary.50' 
                              : !isSlotAvailable 
                                ? 'action.hover'
                                : 'transparent',
                            opacity: isSlotAvailable ? 1 : 0.9,
                            '&:hover': {
                              bgcolor: isSlotAvailable ? (
                                isCurrentTimeSlot ? 'primary.100' : 'action.hover'
                              ) : undefined
                            },
                            cursor: isSlotAvailable ? 'pointer' : 'not-allowed',
                            position: 'relative',
                            boxSizing: 'border-box',
                            // Add visual indicator for unavailable slots
                            ...(!isSlotAvailable && {
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: isDark 
                                  ? 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.02) 4px, rgba(255,255,255,0.02) 8px)'
                                  : 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.02) 4px, rgba(0,0,0,0.02) 8px)',
                                pointerEvents: 'none'
                              }
                            })
                          }}
                        >
                          {isCurrentTimeSlot && (
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                top: '50%',
                                height: 2,
                                bgcolor: 'primary.main',
                                zIndex: 5,
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  left: -3,
                                  top: -4,
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: 'primary.main'
                                }
                              }}
                            />
                          )}
                        </Box>
                      );
                    })}

                    {/* Appointments overlay */}
                    {dayAppointments.map((appointment) => {
                      const style = getAppointmentStyle(appointment, dayAppointments);
                      if (!style) return null;

                      return (
                        <Box
                          key={appointment.id}
                          sx={{
                            position: 'absolute',
                            top: style.top,
                            left: style.left,
                            width: style.width,
                            height: style.height
                          }}
                        >
                          <AppointmentCard
                            appointment={appointment}
                            variant="timeline"
                            showTime={false}
                            height={style.height}
                            isOverlapping={style.left !== 4}
                            onClick={(e) => handleAppointmentClick(appointment, e)}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Appointment Details Modal */}
      <AppointmentSummaryModal
        open={appointmentDetailsOpen}
        onClose={handleAppointmentDetailsClose}
        appointment={selectedAppointment}
        onDataChange={handleAppointmentDataChange}
        availableProducts={availableProducts}
        availableAddons={availableAddons}
      />

      {/* New Appointment Modal */}
      <NewAppointmentModal
        open={newAppointmentOpen}
        onClose={handleNewAppointmentClose}
        garageId={selectedGarage?.id}
        onSuccess={handleAppointmentSuccess}
        onError={(error) => console.error('Appointment creation error:', error)}
        availableProducts={availableProducts}
        availableAddons={availableAddons}
        prefilledDate={selectedSlotDate}
        prefilledTime={selectedSlotTime}
      />
    </Box>
  );
};

export default WeekView;